import React from 'react';
import { motion } from 'framer-motion';
import { Play, Sparkles } from 'lucide-react';
import { type ModuleData, getModuleColors, getModuleIcon } from '../../content';
import { DjayAvatar, type DjayVariant } from '../../mascot';
import { type DjayRecommendation } from '../../mastery';
interface DjayRecommendationCardProps {
  module: ModuleData;
  recommendation: DjayRecommendation;
  onStart: () => void;
  djayVariant?: DjayVariant;
}
export function DjayRecommendationCard({
  module,
  recommendation,
  onStart,
  djayVariant = 'default'
}: DjayRecommendationCardProps) {
  const colors = getModuleColors(module.color);
  const Icon = getModuleIcon(module.icon);
  return <motion.div whileHover={{
    y: -4,
    scale: 1.01
  }} whileTap={{
    scale: 0.98
  }} className="relative cursor-pointer group" onClick={onStart} data-id="element-1352">
      <div className={`relative overflow-hidden rounded-[2rem] bg-gradient-to-r ${colors.from} ${colors.to} p-1 shadow-lg`} data-id="element-1353">
        <div className="bg-white/10 backdrop-blur-sm rounded-[1.8rem] p-4 sm:p-6 flex flex-col sm:flex-row items-center gap-4 sm:gap-6 relative z-10" data-id="element-1354">
          {/* Left: Djay Avatar */}
          <div className="flex-shrink-0 relative" data-id="element-1355">
            <div className="absolute -inset-4 bg-white/20 rounded-full blur-xl" data-id="element-1356" />
            <DjayAvatar size="lg" emotion={recommendation.emotion} variant={djayVariant} animate data-id="element-1357" />
          </div>

          {/* Center: Text Content */}
          <div className="flex-1 text-center sm:text-left text-white" data-id="element-1358">
            <div className="flex items-center justify-center sm:justify-start gap-1.5 mb-1 sm:mb-2" data-id="element-1359">
              <Sparkles className="w-4 h-4 text-yellow-300" data-id="element-1360" />
              <span className="text-white/90 text-xs sm:text-sm font-bold uppercase tracking-wider" data-id="element-1361">
                Djay raadt aan
              </span>
            </div>
            <h3 className="text-xl sm:text-3xl font-black tracking-tight mb-1 sm:mb-2 drop-shadow-sm leading-tight flex items-center justify-center sm:justify-start gap-2" data-id="element-1362">
              <Icon className="w-6 h-6 sm:w-8 sm:h-8 opacity-80" data-id="element-1363" />
              {module.title}
            </h3>
            <p className="text-white/90 font-medium text-sm sm:text-lg leading-snug max-w-lg" data-id="element-1364">
              {recommendation.reason}
            </p>
          </div>

          {/* Right: Play Button */}
          <div className="flex-shrink-0 mt-2 sm:mt-0" data-id="element-1365">
            <div className="w-14 h-14 sm:w-16 sm:h-16 bg-white rounded-full flex items-center justify-center shadow-lg transform group-hover:scale-110 transition-transform duration-300" data-id="element-1366">
              <Play className={`w-7 h-7 sm:w-8 sm:h-8 ${colors.text} ml-1`} fill="currentColor" data-id="element-1367" />
            </div>
          </div>
        </div>

        {/* Decorative background elements */}
        <div className="absolute top-0 right-0 w-64 h-64 bg-white/20 rounded-full blur-3xl -translate-y-1/2 translate-x-1/4 pointer-events-none" data-id="element-1368" />
        <div className="absolute bottom-0 left-0 w-48 h-48 bg-black/10 rounded-full blur-2xl translate-y-1/4 -translate-x-1/4 pointer-events-none" data-id="element-1369" />
      </div>
    </motion.div>;
}